import './App.css';
import { useEffect, useMemo, useState } from 'react';

const LANGUAGES = ['fr', 'en', 'ar'];

const TRANSLATIONS = {
  fr: {
    name: 'WO INFOS',
    bio: "WO Infos est une société de développement logiciel basée en Tunisie et en Arabie saoudite. Nous concevons des plateformes et applications web et mobiles innovantes, dont CliClaim, notre solution dédiée à la gestion des réclamations et à l'amélioration de l'expérience client.",
    linksTitle: 'Liens',
    website: 'CliClaim',
    facebook: 'Facebook',
    instagram: 'Instagram',
    tiktok: 'TikTok',
    playstore: 'Google Play',
    appstore: 'App Store',
    language: 'Langue',
  },
  en: {
    name: 'WO INFOS',
    bio: 'WO Infos is a software development company based in Tunisia & Saudi Arabia. We build innovative web and mobile platforms and applications, including CliClaim, our dedicated solution for claims management and customer experience improvement.',
    linksTitle: 'Links',
    website: 'CliClaim',
    facebook: 'Facebook',
    instagram: 'Instagram',
    tiktok: 'TikTok',
    playstore: 'Google Play',
    appstore: 'App Store',
    language: 'Language',
  },
  ar: {
    name: 'WO INFOS',
    bio: 'WO Infos شركة تطوير برمجيات مقرها في تونس والسعودية. نبني منصات وتطبيقات ويب وموبايل مبتكرة، من بينها CliClaim، حلّنا المتخصص في إدارة الشكاوى والمطالبات وتحسين تجربة العملاء.',
    linksTitle: 'الروابط',
    website: 'موقع CliClaim',
    facebook: 'فيسبوك',
    instagram: 'إنستغرام',
    tiktok: 'تيك توك',
    playstore: 'جوجل بلاي',
    appstore: 'آب ستور',
    language: 'اللغة',
  },
};

function SocialIconImage({ src, alt, style, className }) {
  return (
    <img
      src={src}
      alt={alt}
      className={['lb-linkIconImg', className].filter(Boolean).join(' ')}
      style={style}
    />
  );
}

const SOCIAL_LINKS = [
  {
    id: 'website',
    href: 'https://cliclaim.com',
    icon: (
      <SocialIconImage
        src={`${process.env.PUBLIC_URL}/assets/logo.png`}
        alt="CliClaim"
        className="isLarge"
      />
    ),
    iconBg: 'dark',
  },
  {
    id: 'facebook',
    href: 'https://www.facebook.com/profile.php?id=61588588681819',
    icon: (
      <SocialIconImage
        src={`${process.env.PUBLIC_URL}/assets/facebook.png`}
        alt="Facebook"
      />
    ),
    iconBg: 'facebook',
  },
  {
    id: 'instagram',
    href: 'https://www.instagram.com/cli_claim/',
    icon: (
      <SocialIconImage
        src={`${process.env.PUBLIC_URL}/assets/instagram.png`}
        alt="Instagram"
      />
    ),
    iconBg: 'white',
  },
  {
    id: 'tiktok',
    href: 'https://www.tiktok.com/@cliclaim',
    icon: (
      <SocialIconImage
        src="https://cdn.jsdelivr.net/npm/simple-icons@v11/icons/tiktok.svg"
        alt="TikTok"
        style={{ filter: 'invert(1)' }}
      />
    ),
    iconBg: 'dark',
  },
  {
    id: 'playstore',
    href: 'https://play.google.com/store/apps/details?id=com.CliClaim.Client',
    icon: (
      <SocialIconImage
        src={`${process.env.PUBLIC_URL}/assets/pls.png`}
        alt="Google Play"
      />
    ),
    iconBg: 'white',
  },
  {
    id: 'appstore',
    href: 'https://apps.apple.com/tn/app/cliclaim-client/id6749679124?l=fr-FR',
    icon: (
      <SocialIconImage
        src={`${process.env.PUBLIC_URL}/assets/Aps.png`}
        alt="App Store"
      />
    ),
    iconBg: 'white',
  },
];

function getInitialLanguage() {
  const stored = localStorage.getItem('lang');
  if (stored && LANGUAGES.includes(stored)) return stored;
  const nav = (navigator.language || '').toLowerCase();
  if (nav.startsWith('ar')) return 'ar';
  if (nav.startsWith('en')) return 'en';
  return 'fr';
}

function App() {
  const [lang, setLang] = useState(getInitialLanguage);

  const t = useMemo(() => TRANSLATIONS[lang] ?? TRANSLATIONS.fr, [lang]);
  const isRtl = lang === 'ar';
  const dir = isRtl ? 'rtl' : 'ltr';

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
    document.body.dir = dir;
    localStorage.setItem('lang', lang);
  }, [dir, lang]);

  return (
    <div className="lb" dir={dir}>
      <div className="lb-shell">
        <div className="lb-topbar" aria-label={t.language}>
          <div className="lb-lang">
            {LANGUAGES.map((code) => (
              <button
                key={code}
                type="button"
                className={`lb-langBtn ${lang === code ? 'isActive' : ''}`}
                onClick={() => setLang(code)}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        <main className="lb-card" aria-label="Link bio">
          <div className="lb-profile">
            <div className="lb-avatarWrap" aria-hidden="true">
              <img
                className="lb-avatar"
                src={`${process.env.PUBLIC_URL}/assets/woinfo2.png`}
                alt=""
              />
            </div>
            <h1 className="lb-name">{t.name}</h1>
            <p className="lb-bio">{t.bio}</p>
          </div>

          <section className="lb-links" aria-label={t.linksTitle}>
            {SOCIAL_LINKS.map((item) => (
              <a
                key={item.id}
                className="lb-link"
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span
                  className={[
                    'lb-linkIcon',
                    item.iconBg === 'dark' ? 'isDark' : '',
                    item.iconBg === 'white' ? 'isWhite' : '',
                    item.iconBg === 'facebook' ? 'isFacebook' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  aria-hidden="true"
                >
                  {item.icon}
                </span>
                <span className="lb-linkText">{t[item.id]}</span>
                <span className="lb-linkArrow" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M14 3h7v7h-2V6.4l-9.3 9.3l-1.4-1.4L17.6 5H14V3Zm-9 4h6v2H7v10h10v-4h2v6H5V7Z"
                    />
                  </svg>
                </span>
              </a>
            ))}
          </section>
        </main>
      </div>
    </div>
  );
}

export default App;
